import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * A free shipping progress bar, used on the cart page and inside the cart drawer.
 *
 * The element is registered here, in an external module, rather than in a `<script>` next
 * to the markup. That is deliberate: the cart drawer is re-rendered through
 * `morphSection()` and section hydration, and inline `<script>` tags never execute when
 * HTML is injected that way — a definition living beside the markup would never run for
 * the drawer, leaving the element permanently un-upgraded on its "Loading..." placeholder.
 * Registering once from a module means every instance morphed in upgrades on its own.
 *
 * The markup is server-rendered with the message and bar width already filled in, so the
 * bar is never blank and never shows a placeholder. Liquid has no access to the currency
 * conversion rate though, so for customers shopping in a non-base currency the server's
 * numbers are an approximation that this class corrects.
 *
 * Correcting it is the fiddly part. The drawer's markup gets replaced from the server at
 * several points we don't control — section hydration after first paint, and `morphSection`
 * on every cart change (dispatched *before* `cart:update`'s listeners have had their say).
 * Each replacement reinstates the server's unconverted text. Rather than trying to hook
 * every one of those code paths, the element watches its own subtree and re-applies
 * whenever the message drifts from what it last wrote.
 */
class FreeShippingBar extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** The exact message string this element last wrote, used to detect server overwrites. */
  #lastRenderedMessage = null;

  #observer = new MutationObserver(() => {
    if (this.#messageElement?.textContent !== this.#lastRenderedMessage) this.#sync();
  });

  connectedCallback() {
    this.#abortController = new AbortController();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });

    this.#sync();
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#observer.disconnect();
  }

  get #messageElement() {
    return this.querySelector('[data-message-element]');
  }

  /**
   * Conversion rate from the shop's base currency to the currency being displayed.
   * @returns {number}
   */
  get #currencyRate() {
    const rate = parseFloat(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  /**
   * The free shipping threshold in the displayed currency, in minor units.
   * @returns {number}
   */
  get #threshold() {
    return Math.round(Number(this.dataset.threshold) * this.#currencyRate);
  }

  /** Re-render from the cart total the server last stamped onto the element. */
  #sync() {
    const total = Number(this.dataset.cartTotal);
    if (Number.isFinite(total)) this.render(total);
  }

  /**
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    const total = event?.detail?.resource?.total_price;

    // `cart/change.js` hands back the whole cart, but other sources (add-to-cart, for one)
    // don't always include a total, so fall back to asking the Cart API directly.
    if (typeof total === 'number') {
      this.dataset.cartTotal = String(total);
      this.render(total);
    } else {
      this.#refresh();
    }
  };

  #refresh = async () => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);

      const cart = await response.json();
      this.dataset.cartTotal = String(cart.total_price);
      this.render(cart.total_price);
    } catch (error) {
      // Keep whatever is on screen rather than blanking the bar out.
      console.error('Free shipping bar could not read the cart:', error);
    }
  };

  /**
   * @param {number} cartTotal - The cart total in minor currency units.
   */
  render(cartTotal) {
    const message = this.#messageElement;
    const fill = this.querySelector('[data-progress-fill]');
    const threshold = this.#threshold;

    if (!(message instanceof HTMLElement) || !(fill instanceof HTMLElement) || threshold <= 0) return;

    const remaining = Math.max(0, threshold - cartTotal);
    const percent = Math.min(100, Math.max(0, (cartTotal / threshold) * 100));

    fill.style.width = `${percent}%`;
    this.toggleAttribute('data-unlocked', remaining === 0);

    const text =
      remaining === 0
        ? this.dataset.successMessage ?? ''
        : (this.dataset.messageTemplate ?? '').replace('{amount}', this.#formatMoney(remaining));

    message.textContent = text;
    this.#lastRenderedMessage = text;
  }

  /**
   * @param {number} amount - The amount in minor currency units.
   * @returns {string}
   */
  #formatMoney(amount) {
    const currency = window.Shopify?.currency?.active || this.dataset.currency || 'USD';
    const format = this.dataset.moneyFormat || '{{amount}}';

    return formatMoney(amount, format, currency);
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
